import React, { useState, useEffect, useRef } from "react";
import { animate, AnimationPlaybackControls } from "framer-motion";

import Link from "next/link";
import { ChevronRight } from "lucide-react";

type TotalVoteCountProps = {
  votes: number;
  maxVotes?: number;
};

export function TotalVoteCount({ votes, maxVotes = 100 }: TotalVoteCountProps) {
  // display value for the counter
  const [displayVotes, setDisplayVotes] = useState(votes);
  // progress from 0→1 for the radial fill
  const [progress, setProgress] = useState(0);
  
  // Use refs to store current values without triggering re-renders
  const displayVotesRef = useRef(displayVotes);
  const progressRef = useRef(progress);
  
  // Update refs when state changes
  useEffect(() => {
    displayVotesRef.current = displayVotes;
    progressRef.current = progress;
  }, [displayVotes, progress]);
  
  // Refs to store animation controls
  const countAnimRef = useRef<AnimationPlaybackControls | null>(null);
  const progressAnimRef = useRef<AnimationPlaybackControls | null>(null);
  
  // SVG circle setup
  const size = 160;
  const strokeWidth = 16;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  useEffect(() => {
    // Ensure maxVotes is never zero
    const safeMaxVotes = maxVotes || 100;
    
    // Calculate percentage for progress (clamped between 0-1)
    const targetProgress = Math.min(Math.max(votes / safeMaxVotes, 0), 1);

    // Stop any existing animations
    if (countAnimRef.current) countAnimRef.current.stop();
    if (progressAnimRef.current) progressAnimRef.current.stop();

    // animate the votes count
    countAnimRef.current = animate(displayVotesRef.current, votes, {
      duration: 1.5,
      onUpdate: (latest) => setDisplayVotes(Math.round(latest)),
    });

    // animate the radial progress
    progressAnimRef.current = animate(progressRef.current, targetProgress, {
      duration: 1.5,
      onUpdate: (latest) => setProgress(latest),
    });

    return () => {
      if (countAnimRef.current) countAnimRef.current.stop();
      if (progressAnimRef.current) progressAnimRef.current.stop();
    };
  }, [votes, maxVotes]); // Intentionally omitting displayVotes and progress

  // calculate dash offset based on progress
  const dashOffset = circumference * (1 - progress);

  return (
    <div className="bg-purple-100/70 rounded-2xl px-16 py-5 h-auto flex flex-col items-center shadow-lg backdrop-blur-md">
      {/* Rest of your component remains unchanged */}
      <div className="relative">
        <svg width={size} height={size}>
          {/* gray background circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#d4c0ff"
            strokeWidth={strokeWidth}
            fill="transparent"
          />
          {/* animated purple stroke */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#8a5de4"
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold text-[#8a5de4]">
            {displayVotes.toLocaleString()}
          </span>
          <span className="text-sm text-indigo-900">Votes</span>
        </div>
      </div>
      <p className="my-3 text-indigo-900">Total Vote Counts.</p>
      <Link
        href="/vote#votelist"
        onClick={(e) => {
          e.preventDefault();
          document
            .getElementById("votelist")
            ?.scrollIntoView({ behavior: "smooth" });
        }}
        className=""
      >
        <div className="relative overflow-hidden bg-gradient-to-r from-purple-400 to-indigo-400 px-4 py-3 rounded-full shadow-lg active:scale-95 transition-transform">
          <span className="text-xs font-bold uppercase tracking-wider text-indigo-50 text-nowrap flex justify-center items-center">
            <span className="px-4">Explore Now</span>
            <ChevronRight size={22} className="animate-floattopright" />
          </span>
          <div className="absolute animate-shimmer-swipe-loop inset-0 opacity-100 bg-gradient-to-r from-transparent via-white/70 to-transparent"></div>
        </div>
      </Link>
    </div>
  );
}
